/**
 * versionsStore — single-fetch cache for `geoServer.getVersions`.
 *
 * Lists the versions each dataset the api key can access publishes; loaders filter
 * layers to `version(dataset)`, the user's selection (see `select`) or else the newest.
 *
 * Lifecycle mirrors operationalLayersStore: the root layout calls `load(apiKey)`
 * whenever `apiKey` changes; `load` dedupes per key and short-circuits on
 * success; `clear()` resets when the key is removed.
 */

import type { TVersion } from '@safesystem/geoserver-api';
import { geoServer } from '#lib/config.js';
import { apiKeyStore } from '#lib/stores/apiKeyStore.svelte.js';
import { FetchStatus } from '#lib/types.js';
import { getLogger } from '@logtape/logtape';

const log = getLogger(['platform', 'versions-store']);

// Newest first, numeric-aware (2.10 above 2.3); a release outranks its own prerelease (`2.4.0` above `2.4.0-rc`).
export function compareVersionsDesc(a: string, b: string): number {
	const [aBase, ...aPre] = a.split('-');
	const [bBase, ...bPre] = b.split('-');
	return (
		bBase.localeCompare(aBase, undefined, { numeric: true }) ||
		Number(!bPre.length) - Number(!aPre.length) ||
		bPre.join('-').localeCompare(aPre.join('-'), undefined, { numeric: true })
	);
}

// A layer is in version `v` when its `build` keyword matches: a series can publish several builds (e.g. an RC and its release), so series alone is ambiguous. Layers without a build keyword fall back to the series.
export function isLayerInVersion(
	v: TVersion | undefined,
	layer: { series: string; keywords: Record<string, string> }
): boolean {
	if (!v) return false;
	return layer.keywords.build ? layer.keywords.build === v.build : layer.series === v.series;
}

function createVersionsStore() {
	let versions = $state<TVersion[]>([]);
	let status = $state<FetchStatus>(FetchStatus.IDLE);
	let error = $state<string | null>(null);
	// dataset → user-selected `version` string; absent = use the first row.
	let selected = $state<Record<string, string>>({});
	let loadedKey: string | null = null;
	let inFlight: Promise<void> | null = null;

	// dataset → its versions, highest version first (so `version()` defaults to
	// the newest and the selector lists newest → oldest).
	const byDataset = $derived.by<Record<string, TVersion[]>>(() => {
		const groups: Record<string, TVersion[]> = {};
		for (const v of versions) (groups[v.dataset] ??= []).push(v);
		for (const list of Object.values(groups)) {
			list.sort((a, b) => compareVersionsDesc(a.version, b.version));
		}
		return groups;
	});

	return {
		get versions() {
			return versions;
		},
		get status() {
			return status;
		},
		get error() {
			return error;
		},
		/** Every version a dataset publishes, highest first. */
		versionsFor(dataset: string): TVersion[] {
			return byDataset[dataset] ?? [];
		},
		/** The selected version row for a dataset (or the highest), else undefined. */
		version(dataset: string): TVersion | undefined {
			const list = byDataset[dataset] ?? [];
			const chosen = selected[dataset];
			return (chosen ? list.find((v) => v.version === chosen) : undefined) ?? list[0];
		},
		/** Choose which version of a dataset `version()` returns. */
		select(dataset: string, version: string) {
			selected = { ...selected, [dataset]: version };
		},

		async load(apiKey: string): Promise<void> {
			if (status === FetchStatus.SUCCESS && loadedKey === apiKey) return;
			if (inFlight && loadedKey === apiKey) return inFlight;

			const cred = apiKeyStore.credentials;
			if (!cred) {
				log.error('load: no credentials — validate the api key first');
				return;
			}

			loadedKey = apiKey;
			status = FetchStatus.LOADING;
			error = null;

			inFlight = (async () => {
				try {
					const result = (await geoServer.getVersions(apiKey, cred.namespace)) ?? [];
					if (loadedKey !== apiKey) return;
					versions = result;
					status = FetchStatus.SUCCESS;
					log.info('Loaded versions for {datasetCount} datasets', {
						datasetCount: new Set(result.map((v) => v.dataset)).size
					});
				} catch (err) {
					if (loadedKey !== apiKey) return;
					versions = [];
					status = FetchStatus.FAILURE;
					error = err instanceof Error ? err.message : String(err);
					log.error('Load error: {error}', { error });
				} finally {
					inFlight = null;
				}
			})();

			return inFlight;
		},

		clear() {
			versions = [];
			selected = {};
			status = FetchStatus.IDLE;
			error = null;
			loadedKey = null;
			inFlight = null;
		}
	};
}

export const versionsStore = createVersionsStore();
